import { selectParenteralDosesAdministeredByDrugId } from './drugs-administered'
import { createDoseProp } from '../utils/dose-prop'
import {
  removeKeysWithNullishValues,
  replaceObjectRawKeys,
} from '../utils/object'
import { createSelector } from '../utils/selector-utils'

const prop = createDoseProp({
  route: 'iv',
  drugInfoProp: 'parenDrugs',
  mapFn: replaceObjectRawKeys,
})

/**
 * @param {ParenDrug} parenteralDrug The drug we're selecting
 * @returns {any} The changes to apply to the case.
 */
export const actionSelectParenDrug = ({ id, name, unit }) =>
  prop.actionSelect(id, { name, unit })

/**
 * @param {string} id The rxcui ID of the drug we're unselecting
 * @returns {any} The changes to apply to the case.
 */
export const actionUnselectParenDrug = prop.actionUnselect

/**
 * @param {string} id
 * @param {{
 *   route?: ParenDrugRoute
 *   meta?: 'normal' | 'draw' | 'totals'
 *   drawAmountTemp_raw?: string
 *   totalDrawn_raw?: string
 *   wasted_raw?: string
 *   wasteConfirmed?: boolean
 *   amount_raw?: string
 * }} updates
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateParenDrugDosage = (id, updates) =>
  prop.actionUpdateDosage(id, updates)

/**
 * @param {string} drugId The rxcui ID of the drug we're selecting
 * @param {string} amountRaw The amount as a string we are setting temp draw value to.
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateParenDrugTempDraw = (drugId, amountRaw) =>
  prop.actionUpdateDosage(drugId, { drawAmountTemp_raw: amountRaw })

/**
 * @param {string} drugId The rxcui ID of the drug
 * @param {string} amountRaw The amount as a string we are setting amount to.
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateParenDrugTempAmount = (drugId, amountRaw) =>
  prop.actionUpdateDosage(drugId, { amount_raw: amountRaw })

/**
 * @param {string} drugId The rxcui ID of the drug we're selecting
 * @param {ParenDrugRoute} route The administration route as a string we are setting
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateParenDrugRoute = (drugId, route) =>
  prop.actionUpdateDosage(drugId, { route })

/**
 * @param {MacroParenDrug} macroParenDrug
 * @returns
 */
export const actionPresetParenDrug = ({
  id,
  name,
  unit,
  draw,
  dose,
  route,
}) => {
  const toReturn = {
    ...actionSelectParenDrug({ id, name, unit }),
    ...actionUpdateParenDrugRoute(id, route),
    ...actionUpdateParenDrugTempAmount(id, dose),
    ...actionUpdateParenDrugTempDraw(id, draw),
  }

  return toReturn
}

/** @type {CaseSelector<CaseData['parenDrugs']>} */
export const selectCaseParenDrugs = caseData => prop.selectDrugInfoRaw(caseData)
/** @type {CaseSelector<{ [drugId: string]: ParenDrugDosageProcessed }>} */
export const selectParenDrugDosage = caseData => prop.selectDosage(caseData)
/** @type {CaseSelector<ParenDrugDosage[]>} */
export const selectSelectedParenDrugs = caseData =>
  prop.selectSelectedDrugs(caseData)
/** @type {CaseSelector<{ [drugId: string]: ParenDrugDosage }>} */
export const selectSelectedParenDrugsById = caseData =>
  prop.selectSelectedDrugsById(caseData)
/** @type {CaseSelector<MacroParenDrug[]>} */
export const selectParenDrugsForMacro = createSelector(
  selectSelectedParenDrugs,
  selectParenDrugDosage,
  selectParenteralDosesAdministeredByDrugId,
  (drugs, dosage, administeredByDrugId) =>
    drugs.map(({ id, name, unit }) => {
      const { amount_raw, drawAmountTemp_raw, route } = dosage[id] || {}
      const administered = administeredByDrugId[id] || []
      const firstDose = administered[0]
      return removeKeysWithNullishValues(
        {
          id,
          name,
          route: (firstDose && firstDose.route) || route,
          unit,
          draw: drawAmountTemp_raw,
          dose: (firstDose && firstDose.amount_raw) || amount_raw,
        },
        true
      )
    })
)
